import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import api from "../lib/api";
import { getMetaAttribution } from "../lib/metaAttribution";
import { useCurrency } from "../context/CurrencyContext";

/**
 * Inline PayPal Smart Buttons for a gemstone product.
 *
 *   - Fetches /api/paypal/config once → reads Client ID + sandbox/live env.
 *   - createOrder → POST /api/paypal/order (server creates order, returns id)
 *   - onApprove   → POST /api/paypal/capture/:id (server captures funds)
 *   - On success the user is redirected to /checkout/success?paypal=<orderId>.
 *
 * Renders nothing when PayPal is not configured on the backend (graceful).
 */
const PayPalCheckoutButton = ({ gemstoneId, variationId = null, shippingMethod = null, shippingCountry = "", onBeforePay }) => {
  const { currency } = useCurrency();
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/paypal/config")
      .then(({ data }) => { if (!cancelled) setConfig(data); })
      .catch(() => { if (!cancelled) setConfig({ enabled: false }); })
      .finally(() => { if (!cancelled) setLoadingConfig(false); });
    return () => { cancelled = true; };
  }, []);

  if (loadingConfig) {
    return <div data-testid="paypal-loading" className="text-white/40 text-xs font-accent tracking-[0.22em] uppercase py-2">Loading PayPal…</div>;
  }
  if (!config?.enabled || !config?.client_id) {
    return null; // Silent — PayPal not configured on this env
  }

  return (
    <div data-testid="paypal-button-wrap" className="mt-3">
      <PayPalScriptProvider
        options={{
          clientId: config.client_id,
          currency: ["AUD","BRL","CAD","CHF","CZK","DKK","EUR","GBP","HKD","HUF","ILS","JPY","MXN","NOK","NZD","PHP","PLN","SEK","SGD","THB","TWD","USD"].includes((currency || "USD").toUpperCase())
            ? (currency || "USD").toUpperCase()
            : "USD",
          intent: "capture",
          components: "buttons",
          "disable-funding": "credit",
        }}
      >
        <PayPalButtons
          style={{ layout: "horizontal", color: "gold", shape: "rect", label: "paypal", height: 48, tagline: false }}
          createOrder={async () => {
            if (onBeforePay && onBeforePay() === false) {
              throw new Error("Pre-checkout validation failed");
            }
            const { data } = await api.post("/paypal/order", {
              gemstone_id: gemstoneId,
              currency: (currency || "USD").toUpperCase(),
              variation_id: variationId || undefined,
              shipping_method: shippingMethod || undefined,
              shipping_country: shippingCountry || undefined,
              ...getMetaAttribution(),
            });
            return data.id;
          }}
          onApprove={async (data) => {
            try {
              const { data: cap } = await api.post(`/paypal/capture/${data.orderID}`);
              if (cap.status === "completed") {
                toast.success("Payment captured. Redirecting…");
                window.location.href = `/checkout/success?paypal=${data.orderID}`;
              } else {
                toast.error(`PayPal status: ${cap.status}`);
              }
            } catch (err) {
              toast.error(err.response?.data?.detail || "Capture failed");
            }
          }}
          onError={(err) => {
            // Real network/PayPal failure (not user cancel)
            toast.error("PayPal error — please try again or use another method.");
            if (process.env.NODE_ENV !== "production") console.warn("PayPal SDK error:", err);
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalCheckoutButton;
